import { useEffect, useMemo, useState } from "react";
import { getClassesByLevelService } from "../api/landingClass.service";
import { mapListItemToCourse } from "../api/landingClass.mappers";
import { toSortQueryParam } from "../utils/catalogSort.utils";
import { resolveLevel1IdForLevel2 } from "../utils/resolveLevel1Id";
import { slugifyCatalogSegment } from "@/utils/catalogSlug";
import { COURSES_PER_PAGE } from "../constants/landing.constants";
import type { LandingCourse } from "../types/landing.types";

interface UseClassesByLevelOptions {
  levelId: string;
  level1Id?: string;
  search: string;
  ageFilter: string;
  sortBy: string;
  page: number;
}

interface UseClassesByLevelResult {
  courses: LandingCourse[];
  heroBadge: string;
  heroTitle: string;
  totalPages: number;
  isLoading: boolean;
  error: string | null;
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}

export function useClassesByLevel({
  levelId,
  level1Id,
  search,
  ageFilter,
  sortBy,
  page,
}: UseClassesByLevelOptions): UseClassesByLevelResult {
  const debouncedSearch = useDebouncedValue(search, 300);
  const [courses, setCourses] = useState<LandingCourse[]>([]);
  const [heroBadge, setHeroBadge] = useState("");
  const [heroTitle, setHeroTitle] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const sortParam = useMemo(() => toSortQueryParam(sortBy), [sortBy]);

  useEffect(() => {
    if (!levelId) {
      setCourses([]);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const resolvedLevel1Id = await resolveLevel1IdForLevel2(levelId, level1Id);
        if (!resolvedLevel1Id) {
          if (cancelled) return;
          setError("Unable to load classes for this level.");
          setCourses([]);
          setTotalPages(1);
          return;
        }

        const response = await getClassesByLevelService(levelId, {
          level1Id: resolvedLevel1Id,
          search: debouncedSearch.trim() || undefined,
          page,
          pageSize: COURSES_PER_PAGE,
          badge: ageFilter || undefined,
          sortBy: sortParam,
        });

        if (cancelled) return;

        setHeroBadge(response.category ?? "");
        setHeroTitle(response.sub_category ?? "");
        const listingCategorySlug = slugifyCatalogSegment(
          response.sub_category ?? ""
        );
        setCourses(
          (response.data ?? []).map((row) =>
            mapListItemToCourse(row, levelId, listingCategorySlug || undefined)
          )
        );
        setTotalPages(Math.max(1, response.pageInfo?.total_pages ?? 1));
      } catch {
        if (cancelled) return;
        setError("Unable to load classes for this level.");
        setCourses([]);
        setTotalPages(1);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [levelId, level1Id, debouncedSearch, ageFilter, sortParam, page]);

  return {
    courses,
    heroBadge,
    heroTitle,
    totalPages,
    isLoading,
    error,
  };
}
